import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../api';
import { Pagination } from '../components/Pagination';
import { formatMoney, relativeTime } from '../lib/format';
import { useDebouncedValue } from '../lib/useDebouncedValue';
import { useUrlFilters } from '../lib/useUrlFilters';

const PAGE_SIZE = 60;

export function PriceDrops() {
  const [params, setParams] = useUrlFilters();
  const since = Number(params.get('since') ?? '48') || 48;
  const page = Math.max(1, Number(params.get('page') ?? '1') || 1);

  // Input numérico tipeable: draft local + debounce antes de escribir la URL.
  const urlMinDropPct = params.get('minDropPct') ?? '3';
  const [minDropDraft, setMinDropDraft] = useState(urlMinDropPct);
  const debouncedMinDrop = useDebouncedValue(minDropDraft, 400);

  useEffect(() => {
    // '' = el usuario vació el campo para tipear — no persistir hasta que haya valor.
    if (debouncedMinDrop === '') return;
    setParams(
      { minDropPct: debouncedMinDrop === '3' ? '' : debouncedMinDrop },
      { replace: true, resetPage: true },
    );
  }, [debouncedMinDrop, setParams]);

  useEffect(() => {
    if (urlMinDropPct !== debouncedMinDrop) setMinDropDraft(urlMinDropPct);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlMinDropPct]);

  const minDropPct = Number(urlMinDropPct) || 3;

  const drops = useQuery({
    queryKey: ['price-drops', { since, minDropPct, page }],
    queryFn: () => api.priceDrops({
      since,
      minDropPct,
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
    }),
    staleTime: 5 * 60_000,
    placeholderData: (prev) => prev,
  });

  return (
    <>
      <div className="toolbar">
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          Últimas
          <select
            value={since}
            onChange={(e) =>
              setParams(
                { since: e.target.value === '48' ? '' : e.target.value },
                { resetPage: true },
              )
            }
            style={{ marginLeft: 4 }}
          >
            <option value={12}>12 horas</option>
            <option value={24}>24 horas</option>
            <option value={48}>48 horas</option>
            <option value={72}>72 horas</option>
          </select>
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          Bajada mínima
          <input
            type="number"
            min={1}
            max={90}
            value={minDropDraft}
            onChange={(e) => setMinDropDraft(e.target.value)}
            style={{ width: 60, marginLeft: 4 }}
          />
          %
        </label>
        <span style={{ color: 'var(--text-dim)', fontSize: 12 }}>
          Productos cuyo precio actual es menor que el precio que tenían hace {since}h
        </span>
      </div>

      {drops.isLoading && <div className="loading">Buscando bajadas…</div>}
      {drops.isError && (
        <div className="error">Error: {(drops.error as Error).message}</div>
      )}
      {drops.data && drops.data.items.length === 0 && (
        <div className="empty">
          Sin bajadas en las últimas {since}h con al menos {minDropPct}% de descenso.
        </div>
      )}

      <div className="grid">
        {drops.data?.items.map((p) => {
          const dropPct = Math.round(p.drop_ratio * 100);
          const savingCents = p.prev_price_cents - p.current_price_cents;
          return (
            <Link key={p.id} to={`/p/${p.id}`} className="card">
              {p.image_url ? (
                <img className="card-img" src={p.image_url} alt="" loading="lazy" />
              ) : (
                <div className="card-img-fallback">sin imagen</div>
              )}
              <div className="card-name" title={p.name}>
                {p.name}
              </div>
              <div className="card-price-row">
                <span className="card-price">{formatMoney(p.current_price_cents)}</span>
                <span className="card-list">{formatMoney(p.prev_price_cents)}</span>
                <span className="badge badge-discount">-{dropPct}%</span>
              </div>
              <div style={{ fontSize: 12, color: '#4fb3ff', marginTop: 2 }}>
                Ahorrás {formatMoney(savingCents)}
              </div>
              <div className="card-foot">
                <span>{p.store_id}</span>
                {p.category_path?.[0] && <span>{p.category_path[0]}</span>}
              </div>
              <div className="card-seen">Bajó {relativeTime(p.priced_at)}</div>
            </Link>
          );
        })}
      </div>

      {drops.data && (
        <Pagination
          page={page}
          pageSize={PAGE_SIZE}
          total={drops.data.total}
          onChange={(p) => {
            setParams({ page: p === 1 ? '' : String(p) });
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
        />
      )}
    </>
  );
}
